///
/// EarthBrowser
///
/// Created by Matt Giger on 1/5/2026
/// Copyright (c) 2026 Geodata Labs
///

import { type Database } from "@/earthbrowser/database/Database"
import { cMaxRadarVolumeFootprints, type RadarVolumeFootprint } from "@/earthbrowser/datasets/RadarVolumeMask"
import { type TimeLaneDetail } from "@/earthbrowser/datasets/TimeLane"
import { type EarthBrowserContext } from "@/earthbrowser/EarthBrowserContext"
import { isMemoryConstrainedDevice } from "@/earthbrowser/utils/MemoryLog"
import { engineBridge, isPaidEntitlement } from "@/earthbrowser/engineBridge"
import { cFreeHistoryHours, cPaidHistoryHours } from "@/utils/entitlementLimits"
import { type RenderActionSet } from "@stovokor/engine/Renderer"
import { type RenderState } from "@stovokor/engine/RenderState"
import { DatasetLayer, type DatasetDelta, type DatasetManifest, type DatasetSceneObject, type DatasetSyncConfig } from "../DatasetLayer"
import { NexradTable, type NexradScanProperties, type NexradStationFeature } from "./NexradTable"
import { RadarStationNode } from "./RadarStationNode"

/**
 * Volume activation limits. Each active station holds an ~8-frame window of
 * ~14MB atlas textures (~112MB), so the cap bounds worst-case nexrad memory
 * regardless of how many stations sit inside the view frustum.
 */
const cActivationDistanceKm = 1200	// only stations this close to the camera activate
const cDeactivationDistanceKm = cActivationDistanceKm * 1.2	// hysteresis so panning doesn't thrash purge/reload
const cActiveDistanceBias = 0.85	// active stations rank as if closer, stabilizing the top-N set

/** Raw Arrow row shape for the NEXRAD scans feed (only fields we read) */
interface NexradRow {
	id: unknown
	station_id: unknown
	station_name: unknown
	lat: unknown
	lon: unknown
	elevation_m: unknown
	scan_time: unknown
	atlas_url: unknown
	grid_shape: unknown
	bounds_km: unknown
	max_reflectivity: unknown
	coverage_km: unknown
	ingested_at: unknown
}

export class NexradDatasetLayer extends DatasetLayer<NexradStationFeature> {
	private unsubscribeRedux?: () => void
	private readonly maxActiveVolumes: number
	// Last seen entitlement tier, to re-query scans when depth changes
	// (e.g. checkout completes and the 48-hour window unlocks live)
	private lastPaidState = false

	private readonly ebContext: EarthBrowserContext

	constructor(context: EarthBrowserContext) {
		super(context, "nexrad")
		this.ebContext = context
		// Also the number of footprints the flat radar shaders can step back under
		this.maxActiveVolumes = Math.min(isMemoryConstrainedDevice() ? 2 : 4, cMaxRadarVolumeFootprints)
		this.subscribeToVisibility()
	}

	/**
	 * Activate only the closest stations to the camera, hard-capped, before
	 * rendering children. Inactive stations render nothing and hold no atlas
	 * textures; a station activates purely by camera proximity (focusing a
	 * far station flies the camera to it, which activates it on arrival).
	 * Afterwards publish the footprints of the volumes that drew, so the
	 * mosaic and its tiles step back under them (RadarVolumeMask)
	 */
	override render(state: RenderState): RenderActionSet[] {
		if (this.visible) {
			this.updateActiveVolumes(state)
		}
		const actions = super.render(state)

		const drawing: RadarVolumeFootprint[] = []
		for (const node of this.sceneObjects.values()) {
			if (node instanceof RadarStationNode && node.isVolumeActive && node.volumeDrawing) {
				drawing.push(node.volumeFootprint)
			}
		}
		this.ebContext.radarVolumeMask.publish(drawing, performance.now())
		return actions
	}

	private updateActiveVolumes(state: RenderState): void {
		const ranked: Array<{ node: RadarStationNode, biasedDist: number }> = []

		for (const node of this.sceneObjects.values()) {
			if (!(node instanceof RadarStationNode)) continue
			const dist = node.distanceTo(state.camera.position)
			// Hysteresis: an already-active station stays eligible a bit
			// beyond the activation distance and ranks as if slightly
			// closer, so orbiting near a boundary doesn't purge/reload
			const cutoff = node.isVolumeActive ? cDeactivationDistanceKm : cActivationDistanceKm
			if (dist < cutoff) {
				ranked.push({ node, biasedDist: node.isVolumeActive ? dist * cActiveDistanceBias : dist })
			} else {
				node.setVolumeActive(false)
			}
		}

		ranked.sort((a, b) => a.biasedDist - b.biasedDist)
		ranked.forEach((entry, index) => {
			entry.node.setVolumeActive(index < this.maxActiveVolumes)
		})
	}

	private subscribeToVisibility(): void {
		this.visible = engineBridge.getState()?.earthbrowser.visibleDatasets.includes("nexrad") ?? false
		this.lastPaidState = isPaidEntitlement()
		this.unsubscribeRedux = engineBridge.subscribe(() => {
			const state = engineBridge.getState()
			if (!state) return
			const isVisible = state.earthbrowser.visibleDatasets.includes("nexrad")
			if (isVisible !== this.visible) {
				this.visible = isVisible
				if (!isVisible) {
					// Reclaim GPU atlas memory while hidden (deactivation
					// purges); the closest stations re-activate and reload
					// from the image cache when the layer is re-enabled
					for (const node of this.getSceneObjects()) {
						if (node instanceof RadarStationNode) {
							node.setVolumeActive(false)
						}
					}
				}
			}
			if (state.entitlements.isPaid !== this.lastPaidState) {
				this.lastPaidState = state.entitlements.isPaid
				void this.refresh()
			}
		})
	}

	override destroy(): void {
		this.unsubscribeRedux?.()
		this.ebContext.radarVolumeMask.clear()
		super.destroy()
	}

	// ==================== Sync Configuration ====================

	public getSyncConfig(): DatasetSyncConfig {
		return {
			datasetId: "nexrad",
			pollIntervalMs: 60_000, // 1 minute
			needsSync: true,
			supportsDelta: true,
		}
	}

	public async createTable(db: Database): Promise<void> {
		await NexradTable.create(db)
	}

	public async applyCheckpoint(
		manifest: DatasetManifest,
		downloadArrowRows: (url: string) => Promise<unknown[]>
	): Promise<void> {
		const shard = manifest.checkpoint.shards[0]
		if (!shard) return

		const url = this.resolveUrl(shard.url, shard.path)
		const rows = await downloadArrowRows(url)

		const scans = this.parseNexradRows(rows)
		await NexradTable.replaceAll(scans)
	}

	public async applyDelta(
		delta: DatasetDelta,
		downloadArrowRows: (url: string) => Promise<unknown[]>
	): Promise<void> {
		// Upserts
		const upsertsUrl = this.resolveUrl(delta.upserts.url, delta.upserts.path)
		const upsertRows = await downloadArrowRows(upsertsUrl)
		const upserts = this.parseNexradRows(upsertRows)

		if (upserts.length > 0) {
			await NexradTable.upsert(upserts)
		}

		// Deletes
		if (delta.deletes?.path) {
			const deletesUrl = this.resolveUrl(delta.deletes.url, delta.deletes.path)
			const deletesRows = await downloadArrowRows(deletesUrl)
			const deletes = (deletesRows as Array<{ id: number }>).map(r => Number(r.id))
			if (deletes.length > 0) {
				await NexradTable.deleteByIds(deletes)
			}
		}
	}

	private parseNexradRows(rows: unknown[]): NexradScanProperties[] {
		return (rows as NexradRow[]).map(r => ({
			id: Number(r.id),
			station_id: String(r.station_id),
			station_name: String(r.station_name),
			lat: Number(r.lat),
			lon: Number(r.lon),
			elevation_m: r.elevation_m === null || r.elevation_m === undefined ? null : Number(r.elevation_m),
			scan_time: String(r.scan_time),
			atlas_url: String(r.atlas_url),
			grid_shape: this.parseGridShape(r.grid_shape),
			bounds_km: this.parseBoundsKm(r.bounds_km),
			max_reflectivity: r.max_reflectivity === null || r.max_reflectivity === undefined ? null : Number(r.max_reflectivity),
			coverage_km: Number(r.coverage_km),
			ingested_at: String(r.ingested_at),
		}))
	}

	private parseGridShape(value: unknown): string {
		if (typeof value === "string") return value
		if (Array.isArray(value)) return JSON.stringify(value)
		return "[]"
	}

	private parseBoundsKm(value: unknown): string {
		if (typeof value === "string") return value
		if (typeof value === "object" && value !== null) return JSON.stringify(value)
		return "{}"
	}

	// ==================== Scene Objects ====================

	public async queryFeatures(): Promise<NexradStationFeature[]> {
		// Get all scans grouped by station
		const stations = await NexradTable.getStationsWithScans()

		// Tier history gate (Phase 4 depth gates, SUBSCRIPTION_PRICING_MODEL.md):
		// scan cadence varies per station, so filter by time rather than count.
		// Stations whose scans all aged out drop entirely; their nodes rebuild
		// when fresh scans sync. No store (embed builds) means free-tier caps.
		const capHours = isPaidEntitlement() ? cPaidHistoryHours : cFreeHistoryHours
		const cutoffMs = Date.now() - capHours * 3600_000
		const gated = stations
			.map(station => ({ ...station, scans: station.scans.filter(scan => Date.parse(scan.scan_time) >= cutoffMs) }))
			.filter(station => station.scans.length > 0)

		if (gated.length > 0) {
			const totalScans = gated.reduce((sum, s) => sum + s.scans.length, 0)
			console.log(`[NexradDatasetLayer] Loaded ${gated.length} radar station(s) with ${totalScans} scan(s) (${capHours}h window)`)
		}
		return gated
	}

	public createSceneObject(feature: NexradStationFeature): DatasetSceneObject<NexradStationFeature> | undefined {
		return new RadarStationNode(this.context, feature)
	}

	/** Union of scan times across all stations (each scan covers ~5 minutes) */
	public override getAvailableTimeRange(): { startMs: number; endMs: number } | null {
		let startMs = Infinity
		let endMs = -Infinity
		for (const node of this.getSceneObjects()) {
			for (const scan of node.feature.scans) {
				const t = Date.parse(scan.scan_time)
				if (Number.isNaN(t)) continue
				if (t < startMs) startMs = t
				if (t > endMs) endMs = t
			}
		}
		if (!Number.isFinite(startMs)) return null
		return { startMs, endMs: endMs + 5 * 60_000 }
	}

	/**
	 * Scan times across all stations bucketed to the minute, so frame
	 * stepping lands on real scans without one entry per station. Volume
	 * load state isn't tracked per frame, so no loaded spans are reported.
	 */
	public override getTimeLaneDetail(): TimeLaneDetail | null {
		const minutes = new Set<number>()
		for (const node of this.getSceneObjects()) {
			for (const scan of node.feature.scans) {
				const t = Date.parse(scan.scan_time)
				if (!Number.isNaN(t)) minutes.add(Math.floor(t / 60_000))
			}
		}
		if (minutes.size === 0) return null
		return {
			frameTimesMs: Array.from(minutes).sort((a, b) => a - b).map(m => m * 60_000),
			loadedRanges: [],
		}
	}
}

